import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { SettingsCard } from "../../components/SettingsCard";
import { Check, Plus, X } from "untitledui-js-base";
import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo, useState } from "react";
import { PaginatedResponse } from "shared/types";

import { getErrorMessage } from "utils/error";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Loader } from "shared/components/Loader";
import moment from "moment";
import { Webhook, webhookEvents } from "./types";
import { AddWebhookModal } from "./components/AddWebhookModal";
import { DeleteWebhookModal } from "./components/DeleteWebhookModal";
import { toast } from "react-toastify";
import { Switch } from "shared/components/forms/Switch";

export const BoomScreenWebhooks = () => {
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteWebhookId, setDeleteWebhookId] = useState<number | null>(null);

  const webhooksMeta = useQuery<Webhook[]>({
    queryKey: ["WEBHOOKS"],
    queryFn: () =>
      instance
        .get("/portal/v1/webhooks")
        .then(
          ({ data }: { data: PaginatedResponse<Webhook> }) =>
            data.items as Webhook[]
        )
  });

  const { mutate: activateWebhook, isPending: isActivatePending } = useMutation(
    {
      mutationFn: (id: number) =>
        instance.post(`/portal/v1/webhooks/${id}/activate`),
      mutationKey: ["WEBHOOKS"],
      onSuccess: () => {
        webhooksMeta.refetch();
      },
      onError: (err) => {
        toast.error("Error activating webhook: " + getErrorMessage(err));
      }
    }
  );

  const { mutate: deactivateWebhook, isPending: isDeactivatePending } =
    useMutation({
      mutationFn: (id: number) =>
        instance.post(`/portal/v1/webhooks/${id}/deactivate`),
      mutationKey: ["WEBHOOKS"],
      onSuccess: () => {
        webhooksMeta.refetch();
      },
      onError: (err) => {
        toast.error("Error deactivating webhook: " + getErrorMessage(err));
      }
    });

  const { mutate: deleteWebhookModal, isPending: isDeletePending } =
    useMutation({
      mutationFn: () =>
        instance.delete(`/portal/v1/webhooks/${deleteWebhookId}`),
      mutationKey: ["WEBHOOKS"],
      onSuccess: () => {
        setDeleteWebhookId(null);
        setDeleteOpen(false);
        webhooksMeta.refetch();
      },
      onError: (err) => {
        toast.error("Error deleting webhook: " + getErrorMessage(err));
      }
    });

  const columns = useMemo(() => {
    return [
      {
        id: "endpoint",
        accessorFn: (row) => row,
        size: 300,
        header: "Endpoint",
        cell: (row) => {
          const data = row.getValue();
          return (
            <div className="flex gap-4 items-center">
              <Switch
                onChange={() => {
                  if (!isActivatePending && !isDeactivatePending)
                    if (data.status === "active") deactivateWebhook(data.id);
                    else activateWebhook(data.id);
                }}
                checked={data.status === "active"}
              />

              <div>
                <p className="text-sm font-bold text-content-1000 truncate">
                  {data.url}
                </p>
                <p className="text-xs text-content-300 font-medium">
                  {moment(data.updated_at || data.created_at).format("lll")}
                </p>
              </div>
            </div>
          );
        }
      },
      {
        accessorKey: "event_name",
        id: "event",
        header: "Event",
        cell: (row) => (
          <Badge size={BadgeSize.SM} variant={BadgeVariant.BASE}>
            {webhookEvents[row.getValue()]}
          </Badge>
        )
      },
      {
        accessorKey: "token",
        id: "token",
        header: "Secret",
        cell: (row) => {
          if (Boolean(row.getValue()))
            return (
              <div className="bg-success-500 rounded-full h-4 w-4  flex items-center justify-center ml-3">
                <Check size="12" className="stroke-white" />
              </div>
            );

          return (
            <div className="bg-danger-500 rounded-full h-4 w-4 flex items-center justify-center ml-3">
              <X size="12" className="stroke-white" />
            </div>
          );
        }
      },
      {
        header: "",
        accessorFn: (row) => row,
        id: "delete_action",
        size: 0,
        cell: (row) => (
          <Button
            className="w-full"
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => {
              setDeleteWebhookId(row.getValue().id);
              setDeleteOpen(true);
            }}
          >
            Delete
          </Button>
        )
      }
    ];
  }, []);

  const table = useReactTable({
    data: webhooksMeta.data || [],
    columns: columns as ColumnDef<any, any>[],
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <>
      <SettingsCard
        title="Webhooks"
        button={
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            Icon={Plus}
            onClick={() => {
              setAddOpen(true);
            }}
          >
            New webhook
          </Button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="text-start text-sm text-content-400 font-medium px-1"
                      style={{ width: `${header.getSize()}px` }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="py-4 border-b border-alpha-40 last:border-none"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="truncate px-1 py-3.5 text-sm"
                      style={{ width: `${cell.column.getSize()}px` }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {webhooksMeta.isLoading ? (
          <div className="flex justify-center mx-auto pt-2 animate-appear">
            <Loader />
          </div>
        ) : webhooksMeta.data?.length === 0 ? (
          <p className="text-sm text-content-400 mt-4 ml-1">
            No webhooks found
          </p>
        ) : null}
      </SettingsCard>

      <AddWebhookModal
        open={addOpen}
        setOpen={setAddOpen}
        onSuccess={() => webhooksMeta.refetch()}
      />
      <DeleteWebhookModal
        open={deleteOpen}
        isPending={isDeletePending}
        setOpen={setDeleteOpen}
        onSuccess={() => {
          deleteWebhookId && deleteWebhookModal();
        }}
      />
    </>
  );
};
